/*! Copyright © 2026 Kontextlab GmbH. All rights reserved. */

import { a11y } from './a11y.js';
import { getConfig } from './config.js';

export class PopupPlugin {
    constructor(options = {}) {
        this.defaultOptions = {
            root: document.body,
            color: '#A6DDDD',
            topOffset: 20,
            bottomOffset: 20,
            closeOnOverlayClick: true,
            closeOnEsc: true,
            onClose: null,
            // A dialog needs an accessible name; callers pass either a
            // string or the id of the heading already inside it.
            ariaLabel: null,
            ariaLabelledBy: null,
            closeLabel: null
        };

        this.options = { ...this.defaultOptions, ...options };
        this.isOpen = false;
        this._onOpenCallbacks = [];
        this._onCloseCallbacks = [];
        this._styleEl = null;
        this._prevActiveEl = null;
        this._prevHtmlOverflow = '';
        this._prevBodyOverflow = '';
        this._releaseTrap = null;
        this._timers = new Set();

        this.init();
    }

    init() {
        this.createPopupStructure();
        this.setupEventListeners();
        this.applyStyles();
    }

    createPopupStructure() {
        this.popup = document.createElement('div');
        this.popup.className = 'popup-round';
        this.popup.style.display = 'none';
        if (this.options.id) {
            this.popup.id = this.options.id;
        }

        this.overlay = document.createElement('div');
        this.overlay.className = 'popup-round__overlay';

        this.content = document.createElement('div');
        this.content.className = 'popup-round__content';
        // ARIA for dialog
        this.content.setAttribute('role', 'dialog');
        this.content.setAttribute('aria-modal', 'true');
        if (this.options.ariaLabelledBy) {
            this.content.setAttribute(
                'aria-labelledby',
                this.options.ariaLabelledBy
            );
        } else if (this.options.ariaLabel) {
            this.content.setAttribute('aria-label', this.options.ariaLabel);
        }
        this.popup.setAttribute('aria-hidden', 'true');

        this.closeButton = document.createElement('button');
        this.closeButton.type = 'button';
        this.closeButton.className = 'popup-round__close';
        this.closeButton.innerHTML = '&times;';
        // Without this the button announces as the glyph "times".
        this.closeButton.setAttribute(
            'aria-label',
            this.options.closeLabel ||
                getConfig()?.uiText?.a11y?.closeDialog ||
                'Close'
        );

        this.body = document.createElement('div');
        this.body.className = 'popup-round__body';

        this.content.appendChild(this.body);
        this.popup.appendChild(this.overlay);
        this.popup.appendChild(this.closeButton);
        this.popup.appendChild(this.content);

        this.options.root.appendChild(this.popup);
    }

    setupEventListeners() {
        this.closeButton.addEventListener('click', () => this.close());

        if (this.options.closeOnOverlayClick) {
            this.overlay.addEventListener('click', () => this.close());
        }
    }

    applyStyles() {
        const css = `
            .popup-round {
              position: fixed;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              z-index: 1000;
              display: flex;
              justify-content: center;
              align-items: center;
              opacity: 0;
              visibility: hidden;
              transition: opacity 0.3s, visibility 0.3s;
            }
            
            .popup-round__overlay {
              position: absolute;
              top: 0;
              left: 0;
              width: 100%;
              height: 100%;
              background-color: rgba(0, 0, 0, 0.5);
            }
            
            .popup-round__content {
              position: relative;
              z-index: 2;
              background-color: white;
              border-radius: 8px;
              padding: 20px;
              max-width: calc(100vh - ${this.options.topOffset + this.options.bottomOffset}px);
              max-height: calc(100vh - ${this.options.topOffset + this.options.bottomOffset}px);
              overflow-y: auto;
              box-sizing: border-box;
              transform: scale(0);
              transition: all .15s ease-in;
            }
            
            .popup-round__content.active {
              transform: scale(1);
            }
            
            .popup-round__close {
              position: absolute;
              top: 10px;
              right: 10px;
              background: none;
              border: none;
              font-size: 24px;
              cursor: pointer;
              padding: 5px;
              line-height: 1;
              color: white;
            }
            
            .popup-round__body {
              padding: 20px;
              box-sizing: border-box;
              transform: translateY(-20px);
              opacity: 0;
              transition: all .15s ease-in;
              -webkit-overflow-scrolling: touch; /* iOS smooth scrolling */
            }
            
            .popup-round__body.active {
              opacity: 1;
              transform: translateY(0);
            }
            
            .popup-round__body::-webkit-scrollbar {
              width: 5px;
            }
            
            .popup-round__body::-webkit-scrollbar-button:single-button {
              background-color: white;
              display: none;
              height: 16px;
              border: none;
              box-sizing: border-box;
              position: relative;
            }
            
            .popup-round__body::-webkit-scrollbar-track {
              background: white;
            }
            
            .popup-round__body::-webkit-scrollbar-thumb {
              background-color: black;
            }
            
            .popup-round--open {
              opacity: 1;
              visibility: visible;
            }
            
            @media (min-width: 768px) {
              .popup-round__content {
                width: auto;
                border-radius: 50%;
                background-color: ${this.options.color};
                aspect-ratio: 1/1;
                display: flex;
                justify-content: center;
                align-items: center;
                padding: 40px;
              }
            
              .popup-round__body {
                max-width: 80%;
                max-height: 80%;
                overflow-y: auto;
              }
            }
        `;
        if (!this._styleEl) {
            this._styleEl = document.createElement('style');
            document.head.appendChild(this._styleEl);
        }
        this._styleEl.textContent = css;
    }

    /**
     * Run something after a delay, cancellably.
     *
     * The open and close transitions are staged over hundreds of
     * milliseconds. Left running, each stage acts on whatever state the
     * popup has reached by the time it fires rather than the one it was
     * scheduled for.
     *
     * @param {function(): void} callback - What to run.
     * @param {number} delay - Milliseconds to wait.
     */
    _after(callback, delay) {
        const timer = setTimeout(() => {
            this._timers.delete(timer);
            callback();
        }, delay);
        this._timers.add(timer);
    }

    /** Cancel every stage still waiting to run. */
    _clearTimers() {
        this._timers.forEach(clearTimeout);
        this._timers.clear();
    }

    open(content = '') {
        this.body.innerHTML = '';

        if (typeof content === 'string') {
            if (
                content.trim().startsWith('<') &&
                content.trim().endsWith('>')
            ) {
                this.body.innerHTML = content;
            } else {
                // `querySelector` throws on anything that is not valid CSS,
                // so plain prose reaching this branch took the popup down
                // rather than being shown as the text it is.
                let element;
                try {
                    element = document.querySelector(content);
                } catch {
                    element = null;
                }
                if (element) {
                    const clone = element.cloneNode(true);
                    clone.style.display = '';
                    this.body.appendChild(clone);
                } else {
                    this.body.textContent = content;
                }
            }
        } else if (content instanceof HTMLElement) {
            const clone = content.cloneNode(true);
            clone.style.display = '';
            this.body.appendChild(clone);
        } else {
            this.body.innerHTML = '';
        }

        // Save focus and lock background scroll
        this._prevActiveEl = document.activeElement;
        this._prevHtmlOverflow = document.documentElement.style.overflow;
        this._prevBodyOverflow = document.body.style.overflow;
        document.documentElement.style.overflow = 'hidden';
        document.body.style.overflow = 'hidden';

        this.popup.style.display = 'flex';
        // A close still fading out would otherwise reach its last stage
        // and hide the popup that has just been reopened.
        this._clearTimers();
        this._after(() => {
            this.popup.classList.add('popup-round--open');
            this.content.classList.add('active');
            this.popup.setAttribute('aria-hidden', 'false');
            this.isOpen = true;
            this._triggerOnOpen();
        }, 10);
        this._after(() => {
            this.body.classList.add('active');
            // The trap is installed on `this.popup`, not `this.content`:
            // the close button is a sibling of the content wrapper, so a
            // trap scoped to the content would leave it unreachable.
            this._releaseTrap = a11y.trapFocus(this.popup, {
                initialFocus:
                    this.content.querySelector('[autofocus]') ||
                    a11y.getFocusable(this.popup)[0] ||
                    this.closeButton,
                returnFocus: this._prevActiveEl,
                onEscape: this.options.closeOnEsc
                    ? () => this.close()
                    : null
            });
        }, 150);
    }

    /**
     * Release everything this popup holds and take it off the page.
     *
     * A popup dropped without this leaves its staged transitions running,
     * and they reach for a document that may no longer be there.
     */
    destroy() {
        this._clearTimers();
        this._releaseTrap?.();
        this._releaseTrap = null;
        document.documentElement.style.overflow = this._prevHtmlOverflow;
        document.body.style.overflow = this._prevBodyOverflow;
        this.isOpen = false;
        this.popup?.remove();
        this._styleEl?.remove();
        this._styleEl = null;
    }
    onOpen(callback) {
        if (typeof callback === 'function') {
            this._onOpenCallbacks.push(callback);
        }
        return this;
    }
    _triggerOnOpen() {
        if (typeof this.options.onOpen === 'function') {
            this.options.onOpen();
        }

        this._onOpenCallbacks.forEach((callback) => {
            try {
                callback();
            } catch (e) {
                console.error('Popup onOpen callback error:', e);
            }
        });
    }

    close() {
        // Released up front so focus returns to the opener straight away
        // instead of sitting inside a dialog that is fading out.
        this._releaseTrap?.();
        this._releaseTrap = null;
        // An open still in flight would otherwise install its focus trap
        // after this ran, stranding the keyboard in a hidden dialog with
        // nothing left holding the handle to release it.
        this._clearTimers();
        this._after(() => {
            this.body.classList.remove('active');
        }, 100);
        this._after(() => {
            this.content.classList.remove('active');
        }, 200);
        this._after(() => {
            this.popup.classList.remove('popup-round--open');
            this.popup.style.display = 'none';
            this.popup.setAttribute('aria-hidden', 'true');
            // Restore background scroll and focus
            document.documentElement.style.overflow =
                this._prevHtmlOverflow;
            document.body.style.overflow = this._prevBodyOverflow;
            this.isOpen = false;
            this._triggerOnClose();
        }, 300);
    }
    onClose(callback) {
        if (typeof callback === 'function') {
            this._onCloseCallbacks.push(callback);
        }
        return this;
    }
    offClose(callback) {
        this._onCloseCallbacks = this._onCloseCallbacks.filter(
            (cb) => cb !== callback
        );
        return this;
    }
    _triggerOnClose() {
        if (typeof this.options.onClose === 'function') {
            this.options.onClose();
        }

        this._onCloseCallbacks.forEach((callback) => {
            try {
                callback();
            } catch (e) {
                console.error('Popup onClose callback error:', e);
            }
        });
    }

    updateOptions(newOptions) {
        this.options = { ...this.options, ...newOptions };
        this.applyStyles();
    }
}

export default PopupPlugin;
